import { inject, Injectable } from '@angular/core';
import { isDefined } from '@sama/js-core';
import { mapKeys } from '@sama/micro-dash';
import { getShapesForEventTracking } from '../../../../../cross-team-shared/annotation/shared/state/layers/shape-layer-state';
import { getShapeForEventTracking } from '../../../../../cross-team-shared/annotation/shared/state/shape-state';
import { EventTrackingValueWithSource } from '../../../../../cross-team-shared/misc-utils';
import { keys } from '../../../../../cross-team-shared/misc-utils/keys';
import { AnnotationListService } from '../../../../../outputs/annotation/annotation-shared/toolbars/shape-list-and-tag-editor/annotation-list/annotation-list.service';
import { EventTrackingService } from '../../../../../shared/event-tracking/event-tracking.service';
import { IN_PROGRESS_INDEX } from '../../../../../team-apis/core/reserved-shape-indexes';
import { getShapesFromShapeInfo } from '../../../../h3ml/state/task-workspace-state.utils';
import { TaskWorkspaceStore } from '../../../../h3ml/state/task-workspace-store';
import { ObjectScopingService } from '../../../../shared/misc-services/object-scoping.service';
import { ShapeService } from '../../../../shared/misc-services/shape.service';
import { ObjectSelectionService } from './object-selection.service';
import { ShapeSelectionService } from './shape-selection.service';

/**
 * This service is responsible for updating shape and object selection and tracking a single event when it does
 */
@Injectable()
export class SelectionService {
  private shapeSelectionService = inject(ShapeSelectionService);
  private objectSelectionService = inject(ObjectSelectionService);
  private taskWorkspaceStore = inject(TaskWorkspaceStore);
  private eventTrackingService = inject(EventTrackingService);
  private objectScopingService = inject(ObjectScopingService);
  private shapeService = inject(ShapeService);
  private annotationListService = inject(AnnotationListService);

  setSelection(
    objectIds: number[],
    shapeIndexes: number[],
    eventTrackingValue?: EventTrackingValueWithSource,
  ) {
    const hasObjectSelectionChanges =
      this.objectSelectionService.setSelection(objectIds);
    const hasShapeSelectionChanges =
      this.shapeSelectionService.setSelection(shapeIndexes);

    if (
      eventTrackingValue &&
      (hasObjectSelectionChanges || hasShapeSelectionChanges)
    ) {
      this.trackSelectionChangeEvent({ ...eventTrackingValue, method: 'set' });
    }
  }

  setShapeSelection(
    shapeIndexes: number[],
    eventTrackingValue?: EventTrackingValueWithSource,
  ) {
    if (
      shapeIndexes.length === 1 &&
      shapeIndexes[0] !== IN_PROGRESS_INDEX &&
      this.taskWorkspaceStore.isUsingObjects()
    ) {
      const shape = this.shapeService.getShapeStore(shapeIndexes[0]).state();

      const objectIndex =
        this.objectScopingService.getObjectInScopeContainingShape(shape);

      if (isDefined(objectIndex)) {
        this.setSelection([objectIndex], [], eventTrackingValue);
        return;
      }
    }
    this.setSelection([], shapeIndexes, eventTrackingValue);
  }

  toggleObjectSelection(
    objectId: number,
    eventTrackingValue?: EventTrackingValueWithSource,
  ) {
    const becameSelected =
      this.objectSelectionService.toggleSelection(objectId);

    if (eventTrackingValue) {
      this.trackSelectionChangeEvent({
        ...eventTrackingValue,
        method: becameSelected ? 'add' : 'remove',
      });
    }
  }

  toggleShapeSelection(
    shapeIndexes: number[],
    eventTrackingValue?: EventTrackingValueWithSource,
  ) {
    const isNowSelected =
      this.shapeSelectionService.toggleSelection(shapeIndexes);

    if (eventTrackingValue) {
      this.trackSelectionChangeEvent({
        ...eventTrackingValue,
        // fix me if you change this code

        method: isNowSelected ? 'add' : 'remove',
      });
    }
  }

  selectFirstItemInObject(objectId: number) {
    const firstItemInObject =
      this.annotationListService.getFirstItemInObject(objectId);
    if (!firstItemInObject) {
      return;
    }
    if (firstItemInObject.type === 'shape') {
      this.setSelection([], [firstItemInObject.index]);
    } else {
      this.setSelection([firstItemInObject.index], []);
    }
  }

  getSelectedShapeIndexes() {
    const { objects } = this.taskWorkspaceStore.state();
    const childShapeIdsFromSelectedObjects = this.objectSelectionService
      .getNestedSelection()
      // @ts-ignore -- fix me
      .flatMap((objectId) => objects[objectId].childShapeIds);
    const selectedShapeIndexes =
      this.shapeSelectionService.getSelectedShapeIndexes();
    return [...childShapeIdsFromSelectedObjects, ...selectedShapeIndexes];
  }

  private trackSelectionChangeEvent(
    eventTrackingValue: EventTrackingValueWithSource & {
      method: string;
    },
  ) {
    this.eventTrackingService.track({
      id: 'change_shape_selection',
      bucket: 'adjustment',
      ...eventTrackingValue,
      resulting_shape_selection: this.getShapesForEventTracking(),
      resulting_object_selection:
        this.objectSelectionService.getNestedSelection(),
    });
  }

  private getShapesForEventTracking() {
    const allShapes = getShapesFromShapeInfo(
      this.taskWorkspaceStore.state().outputAnnotationInfo,
    );
    const selectedShapeIndexes = keys(
      this.taskWorkspaceStore('shapeSelection').state(),
    );

    const selectedObjectIds = this.objectSelectionService.getNestedSelection();
    const nestedShapes = allShapes.filter(
      (shape) =>
        isDefined(shape.parentId) && selectedObjectIds.includes(shape.parentId),
    );

    return [
      ...getShapesForEventTracking(
        mapKeys(allShapes, (shape) => shape.index),
        selectedShapeIndexes,
      ),
      ...nestedShapes.map(getShapeForEventTracking),
    ];
  }
}
